import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Activity,
  AlertTriangle,
  Box,
  ChevronDown,
  ChevronRight,
  Cpu,
  Link as LinkIcon,
  MapPin,
  Pencil,
  Plus,
  Radio,
  RefreshCw,
  Trash2,
  X,
  Zap,
} from "lucide-react";
import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Modal } from "../components/ui/Modal";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";
import { useAppStore } from "../stores/appStore";
import { formatDate } from "../lib/utils";
import api from "../lib/api";
import {
  type Device,
  type DeviceWithSensors,
  type Sensor,
  type SimulatorChannel,
  createSensor,
  deleteSensor,
  getDevice,
  getSensor,
  listDevices,
  listSimulatorChannels,
} from "../lib/instrumentationApi";
import { CreateDeviceWizard } from "../components/instrumentation/CreateDeviceWizard";
import { SensorDetailDrawer } from "../components/instrumentation/SensorDetailDrawer";
import { DeviceDetailDrawer } from "../components/instrumentation/DeviceDetailDrawer";
import {
  SearchableAssetPicker,
  type AssetOption,
} from "../components/instrumentation/SearchableAssetPicker";

/**
 * Instrumentation page
 *
 * Layout: a tree of devices → child sensors on the left, a unified
 * right sidebar on the right. The sidebar shows EITHER a sensor's
 * detail tabs OR a device's editor, depending on what the user picked.
 * Both edit flows live in the sidebar — no separate edit windows.
 */

type SidebarTarget =
  | { kind: "device"; device: Device }
  | { kind: "sensor"; sensor: Sensor; device: Device }
  | null;

/** Minimal FL shape pulled from /functional-locations. Kept local because
 *  no other Instrumentation surface needs it. */
interface FlocOption {
  id: string;
  code: string;
  name: string;
  level: number;
}

export default function Instrumentation() {
  const { selectedSiteId } = useAppStore();
  const navigate = useNavigate();
  // Path-based routing. The URL IS the source of truth for what's open
  // in the right sidebar — there's no separate "consumed" flag, and we
  // never strip the URL after opening. Closing the sidebar navigates
  // back to /instrumentation; clicking a sensor or device in the tree
  // navigates to /instrumentation/sensors/:id or /instrumentation/devices/:id.
  const { sensorId: sensorIdParam, deviceId: deviceIdParam } = useParams<{
    sensorId?: string;
    deviceId?: string;
  }>();

  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [deviceDetails, setDeviceDetails] = useState<Map<string, DeviceWithSensors>>(
    new Map(),
  );
  const [error, setError] = useState<string | null>(null);

  const [createWizardOpen, setCreateWizardOpen] = useState(false);
  const [sidebarTarget, setSidebarTarget] = useState<SidebarTarget>(null);

  // Add Sensor — kept as a small modal because device + simulator channel
  // defaults are easier to capture in a focused dialog than in the tree.
  // The edit-sensor path is gone; edits happen in the sidebar's Details tab.
  const [addSensorDevice, setAddSensorDevice] = useState<Device | null>(null);
  const [sensorForm, setSensorForm] = useState({
    name: "",
    sensor_type: "",
    unit: "",
    status: "active",
    source_channel: "",
    asset_id: "",
  });
  const [sensorFormError, setSensorFormError] = useState<string | null>(null);
  const [isSavingSensor, setIsSavingSensor] = useState(false);

  const [simChannels, setSimChannels] = useState<SimulatorChannel[]>([]);
  useEffect(() => {
    listSimulatorChannels()
      .then(setSimChannels)
      .catch(() => setSimChannels([]));
  }, []);

  const [assets, setAssets] = useState<AssetOption[]>([]);
  useEffect(() => {
    if (!selectedSiteId) {
      setAssets([]);
      return;
    }
    let cancelled = false;
    api
      .get("/assets", { params: { virtual_site_id: selectedSiteId, page_size: 500 } })
      .then((res) => {
        if (cancelled) return;
        setAssets((res.data?.items || []) as AssetOption[]);
      })
      .catch((err) => {
        // Surface the fetch error in dev tools — silent empties hide
        // backend page-cap regressions (this exact bug bit us once when
        // the backend's le=100 rejected our page_size=500 as 422).
        console.error("Failed to load site assets for picker:", err);
        if (!cancelled) setAssets([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSiteId]);
  const assetById = useMemo(() => {
    const m = new Map<string, AssetOption>();
    for (const a of assets) m.set(a.id, a);
    return m;
  }, [assets]);

  // Functional locations for the current site — used both to render the
  // FL chip next to the Asset chip on each device/sensor row AND to power
  // the FL filter dropdown. /assets returns the FK only, so we resolve
  // FL summaries client-side from this lookup.
  const [flocs, setFlocs] = useState<FlocOption[]>([]);
  useEffect(() => {
    if (!selectedSiteId) {
      setFlocs([]);
      return;
    }
    let cancelled = false;
    api
      .get("/functional-locations", {
        params: { virtual_site_id: selectedSiteId, page_size: 500 },
      })
      .then((res) => {
        if (cancelled) return;
        const items: FlocOption[] = (res.data?.items || []).map((f: FlocOption) => ({
          id: f.id,
          code: f.code,
          name: f.name,
          level: f.level,
        }));
        items.sort((a, b) => a.level - b.level || a.code.localeCompare(b.code));
        setFlocs(items);
      })
      .catch(() => {
        if (!cancelled) setFlocs([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedSiteId]);
  const flocById = useMemo(() => {
    const m = new Map<string, FlocOption>();
    for (const f of flocs) m.set(f.id, f);
    return m;
  }, [flocs]);
  // Per-asset FL resolver. Two-step lookup (asset → asset.functional_location_id
  // → FL summary) is wrapped here so callers don't have to know about the
  // intermediate FK. Returns null when the asset is unlinked or its FL was
  // deleted out from under us.
  const flocForAssetId = useCallback(
    (assetId: string | null | undefined): FlocOption | null => {
      if (!assetId) return null;
      const asset = assetById.get(assetId);
      if (!asset?.functional_location_id) return null;
      return flocById.get(asset.functional_location_id) ?? null;
    },
    [assetById, flocById],
  );

  // Per-column filters. Applied client-side on top of the server-side
  // `search` (which already covers device name/code substring). Empty
  // string = "any value". Asset and FL filters operate on the device's
  // own asset_id — sensor-level overrides aren't searchable here because
  // they'd require expanding every device to know what to match against.
  const [filterSource, setFilterSource] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterFlocId, setFilterFlocId] = useState("");
  const [filterAssetId, setFilterAssetId] = useState("");

  const [pendingSensorDelete, setPendingSensorDelete] = useState<Sensor | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchDevices = useCallback(async () => {
    if (!selectedSiteId) {
      setDevices([]);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const res = await listDevices({
        virtual_site_id: selectedSiteId,
        search: search.trim() || undefined,
        page_size: 100,
      });
      setDevices(res.items);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setError(e.response?.data?.detail || "Failed to load devices.");
      setDevices([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSiteId, search]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const loadDeviceDetail = useCallback(async (deviceId: string) => {
    try {
      const detail = await getDevice(deviceId);
      setDeviceDetails((prev) => {
        const next = new Map(prev);
        next.set(deviceId, detail);
        return next;
      });
    } catch {
      // Ignore — row stays in loading state until a later attempt resolves.
    }
  }, []);

  function toggleExpanded(deviceId: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
      } else {
        next.add(deviceId);
        loadDeviceDetail(deviceId);
      }
      return next;
    });
  }

  // Path-param sync: when the route is /instrumentation/sensors/:sensorId,
  // make sure the sidebar shows that sensor. We resolve the sensor + parent
  // device via the API directly so pagination, an active search filter, or
  // a slow device-list fetch can't hide the target. We splice the device
  // into local state if it's not already there and expand its row in the tree.
  //
  // Idempotency: if the sidebar is already showing this exact sensor, we
  // skip the round-trip — clicks from inside the tree call openSensorInSidebar
  // which sets the target and navigates in the same render.
  useEffect(() => {
    if (!sensorIdParam) return;
    if (!selectedSiteId) return;
    let cancelled = false;
    (async () => {
      try {
        const sensor = await getSensor(sensorIdParam);
        if (cancelled) return;
        const device = await getDevice(sensor.device_id);
        if (cancelled) return;
        setDevices((prev) => {
          if (prev.some((d) => d.id === device.id)) return prev;
          return [device, ...prev];
        });
        setExpanded((prev) => {
          const next = new Set(prev);
          next.add(device.id);
          return next;
        });
        await loadDeviceDetail(device.id);
        if (cancelled) return;
        setSidebarTarget({ kind: "sensor", sensor, device });
      } catch (err) {
        console.warn("Failed to open deep-linked sensor:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sensorIdParam, selectedSiteId, loadDeviceDetail]);

  // Mirror of the sensor effect, for /instrumentation/devices/:deviceId.
  useEffect(() => {
    if (!deviceIdParam) return;
    if (!selectedSiteId) return;
    let cancelled = false;
    (async () => {
      try {
        const device = await getDevice(deviceIdParam);
        if (cancelled) return;
        setDevices((prev) => {
          if (prev.some((d) => d.id === device.id)) return prev;
          return [device, ...prev];
        });
        setExpanded((prev) => {
          const next = new Set(prev);
          next.add(device.id);
          return next;
        });
        await loadDeviceDetail(device.id);
        if (cancelled) return;
        setSidebarTarget({ kind: "device", device });
      } catch (err) {
        console.warn("Failed to open deep-linked device:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [deviceIdParam, selectedSiteId, loadDeviceDetail]);

  // If the route lands on bare /instrumentation, clear any stale sidebar.
  useEffect(() => {
    if (!sensorIdParam && !deviceIdParam) {
      setSidebarTarget(null);
    }
  }, [sensorIdParam, deviceIdParam]);

  // ── Sidebar selection helpers ────────────────────────────────────
  //
  // Setting `sidebarTarget` AND navigating in the same render gives us
  // a snappy open (no API round-trip for a sensor/device we already hold)
  // while keeping the URL in sync so users can copy, share, or refresh
  // the page on a specific sensor/device.

  function openDeviceInSidebar(device: Device) {
    setSidebarTarget({ kind: "device", device });
    navigate(`/instrumentation/devices/${device.id}`, { replace: true });
  }

  function openSensorInSidebar(sensor: Sensor, device: Device) {
    setSidebarTarget({ kind: "sensor", sensor, device });
    navigate(`/instrumentation/sensors/${sensor.id}`, { replace: true });
  }

  function closeSidebar() {
    setSidebarTarget(null);
    navigate("/instrumentation", { replace: true });
  }

  // After a sidebar edit / delete, refresh the device list and the
  // expanded device's sensor list so the tree stays consistent.
  const refreshAfterChange = useCallback(async () => {
    await fetchDevices();
    if (sidebarTarget?.kind === "device") {
      await loadDeviceDetail(sidebarTarget.device.id);
    } else if (sidebarTarget?.kind === "sensor") {
      await loadDeviceDetail(sidebarTarget.device.id);
    }
  }, [fetchDevices, sidebarTarget, loadDeviceDetail]);

  // ── Add Sensor ───────────────────────────────────────────────────

  function openAddSensor(device: Device) {
    setAddSensorDevice(device);
    const inferredChannel =
      device.source_type === "simulated"
        ? device.device_type?.includes("vibration")
          ? "vibration_motor"
          : device.device_type?.includes("temperature")
            ? "temperature_outdoor"
            : device.device_type?.includes("humidity")
              ? "humidity_indoor"
              : ""
        : "";
    setSensorForm({
      name: "",
      sensor_type: "",
      unit: "",
      status: "active",
      source_channel: inferredChannel,
      asset_id: device.asset_id ?? "",
    });
    setSensorFormError(null);
  }

  async function submitSensorForm() {
    if (!addSensorDevice) return;
    if (!sensorForm.name.trim()) {
      setSensorFormError("Name is required.");
      return;
    }
    setIsSavingSensor(true);
    setSensorFormError(null);
    try {
      const fresh = await createSensor({
        device_id: addSensorDevice.id,
        name: sensorForm.name.trim(),
        sensor_type: sensorForm.sensor_type.trim() || null,
        unit: sensorForm.unit.trim() || null,
        status: sensorForm.status,
        source_channel: sensorForm.source_channel.trim() || null,
        asset_id: sensorForm.asset_id || null,
      });
      setAddSensorDevice(null);
      if (expanded.has(addSensorDevice.id)) {
        await loadDeviceDetail(addSensorDevice.id);
      }
      await fetchDevices();
      // Open the new sensor in the sidebar so the user lands right in
      // its Details tab to finish configuring it.
      openSensorInSidebar(fresh, addSensorDevice);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      setSensorFormError(e.response?.data?.detail || "Failed to save sensor.");
    } finally {
      setIsSavingSensor(false);
    }
  }

  // ── Sensor delete (from tree row) ────────────────────────────────

  async function confirmDeleteSensor() {
    if (!pendingSensorDelete) return;
    setIsDeleting(true);
    try {
      await deleteSensor(pendingSensorDelete.id);
      const did = pendingSensorDelete.device_id;
      if (
        sidebarTarget?.kind === "sensor" &&
        sidebarTarget.sensor.id === pendingSensorDelete.id
      ) {
        closeSidebar();
      }
      setPendingSensorDelete(null);
      if (expanded.has(did)) {
        await loadDeviceDetail(did);
      }
      await fetchDevices();
    } finally {
      setIsDeleting(false);
    }
  }

  // Distinct enum values from the loaded devices — populates the source +
  // status filter dropdowns so users only see options that are actually
  // present, not the full domain enum. Sorted for stable ordering.
  const sourceOptions = useMemo(() => {
    const s = new Set<string>();
    for (const d of devices) if (d.source_type) s.add(d.source_type);
    return Array.from(s).sort();
  }, [devices]);
  const statusOptions = useMemo(() => {
    const s = new Set<string>();
    for (const d of devices) if (d.status) s.add(d.status);
    return Array.from(s).sort();
  }, [devices]);

  // Client-side filtered device list. The server-side `search` parameter
  // already covers name substring; this layers source/status/FL/asset on
  // top of that result set.
  const filteredDevices = useMemo(() => {
    return devices.filter((d) => {
      if (filterSource && d.source_type !== filterSource) return false;
      if (filterStatus && d.status !== filterStatus) return false;
      if (filterAssetId && d.asset_id !== filterAssetId) return false;
      if (filterFlocId) {
        const fl = flocForAssetId(d.asset_id);
        if (fl?.id !== filterFlocId) return false;
      }
      return true;
    });
  }, [devices, filterSource, filterStatus, filterAssetId, filterFlocId, flocForAssetId]);

  const activeFilterCount =
    (filterSource ? 1 : 0) +
    (filterStatus ? 1 : 0) +
    (filterFlocId ? 1 : 0) +
    (filterAssetId ? 1 : 0);

  function clearFilters() {
    setFilterSource("");
    setFilterStatus("");
    setFilterFlocId("");
    setFilterAssetId("");
  }

  const totalSensors = useMemo(
    () => filteredDevices.reduce((sum, d) => sum + (d.sensor_count || 0), 0),
    [filteredDevices],
  );
  const activeDevices = useMemo(
    () => filteredDevices.filter((d) => d.status === "active").length,
    [filteredDevices],
  );
  const liveDevices = useMemo(
    () => filteredDevices.filter((d) => d.connection_status === "connected").length,
    [filteredDevices],
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="-mx-6 -my-6 flex h-[calc(100vh-4rem)] flex-col bg-surface-950"
    >
      <div className="flex items-center justify-between border-b border-surface-700/50 bg-surface-900/60 px-6 py-3 backdrop-blur-xl">
        <div>
          <h1 className="text-xl font-bold text-surface-100">Instrumentation</h1>
          <p className="mt-0.5 text-xs text-surface-400">
            Devices, sensors, and IoT data sources. Click a device or sensor to edit it in the sidebar.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="icon" onClick={fetchDevices} title="Refresh">
            <RefreshCw className={"h-4 w-4 " + (isLoading ? "animate-spin" : "")} />
          </Button>
          <Button onClick={() => setCreateWizardOpen(true)} disabled={!selectedSiteId}>
            <Plus className="mr-1.5 h-4 w-4" />
            New device
          </Button>
        </div>
      </div>

      {/* Per-column filter bar — one input per searchable column. Name is
          server-side (the only fast path for substring across the full
          list); source / status / FL / asset are client-side filters on
          the already-loaded device list. */}
      <div className="flex flex-wrap items-end gap-2 border-b border-surface-700/50 bg-surface-900/40 px-6 py-2.5">
        <FilterField label="Name" widthClass="w-48">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search devices…"
            className="w-full rounded-md border border-surface-700 bg-surface-900 px-2 py-1.5 text-xs text-surface-100 placeholder:text-surface-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          />
        </FilterField>
        <FilterField label="Source" widthClass="w-36">
          <FilterSelect
            value={filterSource}
            onChange={setFilterSource}
            placeholder="Any source"
            options={sourceOptions.map((s) => ({ value: s, label: s.replace(/_/g, " ") }))}
          />
        </FilterField>
        <FilterField label="Status" widthClass="w-32">
          <FilterSelect
            value={filterStatus}
            onChange={setFilterStatus}
            placeholder="Any status"
            options={statusOptions.map((s) => ({ value: s, label: s }))}
          />
        </FilterField>
        <FilterField label="Functional Location" widthClass="w-52">
          <FilterSelect
            value={filterFlocId}
            onChange={setFilterFlocId}
            placeholder="Any FL"
            options={flocs.map((f) => ({
              value: f.id,
              label: `L${f.level} · ${f.code} — ${f.name}`,
            }))}
          />
        </FilterField>
        <FilterField label="Asset" widthClass="w-52">
          <FilterSelect
            value={filterAssetId}
            onChange={setFilterAssetId}
            placeholder="Any asset"
            options={assets.map((a) => ({
              value: a.id,
              label: `${a.asset_code} — ${a.name}`,
            }))}
          />
        </FilterField>
        {activeFilterCount > 0 && (
          <button
            type="button"
            onClick={clearFilters}
            className="ml-1 inline-flex h-7 items-center gap-1 rounded-md border border-surface-700 bg-surface-900 px-2 text-[11px] font-medium text-surface-300 hover:border-surface-500 hover:text-surface-100"
            title="Clear all column filters"
          >
            <X className="h-3 w-3" />
            Clear filters ({activeFilterCount})
          </button>
        )}
        <div className="ml-auto self-center text-[11px] text-surface-500">
          {filteredDevices.length} of {devices.length} device{devices.length === 1 ? "" : "s"}
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-4 gap-3 border-b border-surface-700/50 bg-surface-900/30 px-6 py-3">
        <StatCard label="Devices" value={devices.length} icon={Cpu} />
        <StatCard label="Sensors" value={totalSensors} icon={Radio} />
        <StatCard label="Active" value={activeDevices} icon={Activity} />
        <StatCard label="Live" value={liveDevices} icon={Zap} tone="ok" />
      </div>

      <div className="flex flex-1 overflow-hidden">
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {!selectedSiteId && (
            <div className="mb-3 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              Select a virtual site from the top bar to manage instrumentation devices and sensors.
            </div>
          )}
          {error && (
            <div className="mb-3 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              <AlertTriangle className="mr-2 inline h-4 w-4" />
              {error}
            </div>
          )}
          {devices.length === 0 && !isLoading && selectedSiteId && (
            <div className="rounded-xl border border-dashed border-surface-700 bg-surface-900/30 px-6 py-12 text-center text-sm text-surface-500">
              No devices yet. Click "New device" to add the first one.
            </div>
          )}
          {devices.length > 0 && filteredDevices.length === 0 && (
            <div className="rounded-xl border border-dashed border-surface-700 bg-surface-900/30 px-6 py-8 text-center text-sm text-surface-500">
              No devices match the current filters.{" "}
              <button
                onClick={clearFilters}
                className="text-brand-300 hover:text-brand-200"
              >
                Clear filters
              </button>
              .
            </div>
          )}

          <div className="space-y-2">
            {filteredDevices.map((device) => {
              const isExpanded = expanded.has(device.id);
              const detail = deviceDetails.get(device.id);
              const sensors = detail?.sensors;
              const linkedAsset = device.asset_id
                ? assetById.get(device.asset_id) ?? null
                : null;
              const linkedFloc = flocForAssetId(device.asset_id);
              const isSelected =
                sidebarTarget?.kind === "device" &&
                sidebarTarget.device.id === device.id;
              return (
                <div
                  key={device.id}
                  className={
                    "overflow-hidden rounded-xl border bg-surface-900/40 " +
                    (isSelected
                      ? "border-brand-500/50 ring-1 ring-brand-500/30"
                      : "border-surface-700/50")
                  }
                >
                  <div className="flex items-center gap-1.5 px-2 py-1.5">
                    <button
                      onClick={() => toggleExpanded(device.id)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-surface-400 hover:bg-white/5 hover:text-surface-100"
                      title={isExpanded ? "Collapse" : "Expand to view sensors"}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5" />
                      )}
                    </button>
                    <div
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gradient-to-br from-brand-500/30 to-purple-500/30 ring-1 ring-white/10"
                      title="Device"
                    >
                      <Cpu className="h-3.5 w-3.5 text-brand-200" />
                    </div>
                    <button
                      onClick={() => openDeviceInSidebar(device)}
                      className="min-w-0 flex-1 text-left"
                      title="Open device details in the sidebar"
                    >
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="truncate text-sm font-semibold text-surface-100">
                          {device.name}
                        </span>
                        <SourcePill source={device.source_type} />
                        {/* FL chip sits to the LEFT of the asset chip so
                            users can read "where" (FL) before "what" (asset). */}
                        {linkedFloc && (
                          <FlocPill
                            label={`${linkedFloc.code} — ${linkedFloc.name}`}
                            level={linkedFloc.level}
                          />
                        )}
                        {linkedAsset && (
                          <AssetLinkPill
                            label={`${linkedAsset.asset_code} · ${linkedAsset.name}`}
                            assetId={linkedAsset.id}
                          />
                        )}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px] text-surface-400">
                        <span>
                          {device.sensor_count} sensor{device.sensor_count === 1 ? "" : "s"}
                        </span>
                        {device.device_type && <span>· {device.device_type}</span>}
                        {device.manufacturer && <span>· {device.manufacturer}</span>}
                      </div>
                    </button>
                    <ConnectionBadge
                      status={device.connection_status}
                      lastSeen={device.last_seen_at}
                    />
                    <DeviceStatusBadge status={device.status} />
                    <div className="flex shrink-0 items-center gap-0.5">
                      <IconButton onClick={() => openAddSensor(device)} title="Add sensor">
                        <Plus className="h-3.5 w-3.5" />
                      </IconButton>
                      <IconButton
                        onClick={() => openDeviceInSidebar(device)}
                        title="Edit device in sidebar"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </IconButton>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-surface-700/40 bg-surface-950/40">
                      {!sensors && (
                        <div className="px-12 py-3 text-xs text-surface-500">
                          Loading sensors…
                        </div>
                      )}
                      {sensors && sensors.length === 0 && (
                        <div className="px-12 py-4 text-center text-xs text-surface-500">
                          No sensors on this device.{" "}
                          <button
                            onClick={() => openAddSensor(device)}
                            className="text-brand-300 hover:text-brand-200"
                          >
                            Add the first one
                          </button>
                          .
                        </div>
                      )}
                      {sensors?.map((sensor) => {
                        const linked = sensor.asset_id
                          ? assetById.get(sensor.asset_id) ?? null
                          : null;
                        const linkedSensorFloc = flocForAssetId(sensor.asset_id);
                        const isSensorSelected =
                          sidebarTarget?.kind === "sensor" &&
                          sidebarTarget.sensor.id === sensor.id;
                        return (
                          <TreeSensorRow
                            key={sensor.id}
                            sensor={sensor}
                            linkedAsset={linked}
                            linkedFloc={linkedSensorFloc}
                            isSelected={isSensorSelected}
                            onSelect={() => openSensorInSidebar(sensor, device)}
                            onDelete={() => setPendingSensorDelete(sensor)}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Right sidebar — unified target. Renders the device editor or
            the sensor detail tabs, never both at once. Always visible
            (no `hidden lg:flex` gate) so deep-links like /instrumentation/
            sensors/:id open the drawer on any screen width. Narrow screens
            get a slimmer panel rather than no panel. */}
        <aside className="flex w-full max-w-[460px] shrink-0 flex-col border-l border-surface-700/50 sm:w-[420px] lg:w-[460px]">
          {sidebarTarget?.kind === "device" ? (
            <DeviceDetailDrawer
              key={sidebarTarget.device.id}
              device={sidebarTarget.device}
              assets={assets}
              onClose={closeSidebar}
              onChanged={refreshAfterChange}
              onDeleted={async () => {
                const deletedId = sidebarTarget.device.id;
                closeSidebar();
                setExpanded((prev) => {
                  const next = new Set(prev);
                  next.delete(deletedId);
                  return next;
                });
                setDeviceDetails((prev) => {
                  const next = new Map(prev);
                  next.delete(deletedId);
                  return next;
                });
                await fetchDevices();
              }}
              onAddSensor={() => openAddSensor(sidebarTarget.device)}
              onSelectSensor={(sensor, device) =>
                openSensorInSidebar(sensor, device)
              }
            />
          ) : sidebarTarget?.kind === "sensor" ? (
            <SensorDetailDrawer
              key={sidebarTarget.sensor.id}
              sensor={sidebarTarget.sensor}
              device={sidebarTarget.device}
              variant="pane"
              onClose={closeSidebar}
              onChanged={refreshAfterChange}
            />
          ) : (
            <EmptySidebar />
          )}
        </aside>
      </div>

      {selectedSiteId && (
        <CreateDeviceWizard
          isOpen={createWizardOpen}
          onClose={() => setCreateWizardOpen(false)}
          virtualSiteId={selectedSiteId}
          assets={assets}
          onCreated={() => {
            setCreateWizardOpen(false);
            fetchDevices();
          }}
        />
      )}

      {/* Add Sensor modal — creation-only. Editing happens inline in the
          sidebar's Details tab. */}
      <Modal
        isOpen={addSensorDevice !== null}
        onClose={() => setAddSensorDevice(null)}
        title={`Add sensor · ${addSensorDevice?.name ?? ""}`}
      >
        <div className="space-y-3">
          <Input
            label="Name"
            value={sensorForm.name}
            onChange={(e) => setSensorForm({ ...sensorForm, name: e.target.value })}
            placeholder="e.g. X-axis acceleration"
          />
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Type"
              value={sensorForm.sensor_type}
              onChange={(e) => setSensorForm({ ...sensorForm, sensor_type: e.target.value })}
              placeholder="e.g. temperature"
            />
            <Input
              label="Unit"
              value={sensorForm.unit}
              onChange={(e) => setSensorForm({ ...sensorForm, unit: e.target.value })}
              placeholder="e.g. °C, bar, mm/s"
            />
          </div>
          {addSensorDevice?.source_type === "simulated" ? (
            <div>
              <label className="mb-1.5 block text-sm font-medium text-surface-200">
                Simulator channel
              </label>
              <select
                value={sensorForm.source_channel}
                onChange={(e) =>
                  setSensorForm({ ...sensorForm, source_channel: e.target.value })
                }
                className="w-full rounded-lg border border-surface-700 bg-surface-900 px-3 py-2 text-sm text-surface-100 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                <option value="">— Auto from sensor type —</option>
                {simChannels.map((c) => (
                  <option key={c.key} value={c.key}>
                    {c.label} ({c.unit})
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <Input
              label="Source channel"
              value={sensorForm.source_channel}
              onChange={(e) =>
                setSensorForm({ ...sensorForm, source_channel: e.target.value })
              }
              placeholder="Upstream tag / topic name (optional)"
            />
          )}
          <Input
            label="Status"
            value={sensorForm.status}
            onChange={(e) => setSensorForm({ ...sensorForm, status: e.target.value })}
            placeholder="active / inactive / faulty / calibrating"
          />
          <SearchableAssetPicker
            label="Linked asset"
            assets={assets}
            value={sensorForm.asset_id}
            onChange={(id) => setSensorForm({ ...sensorForm, asset_id: id ?? "" })}
            help="Defaults to the parent device's asset. Override here to map this sensor to a different asset."
          />
          {sensorFormError && <p className="text-xs text-red-400">{sensorFormError}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setAddSensorDevice(null)}>
              Cancel
            </Button>
            <Button onClick={submitSensorForm} isLoading={isSavingSensor}>
              Create sensor
            </Button>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={pendingSensorDelete !== null}
        title="Delete sensor?"
        message={pendingSensorDelete ? `Delete ${pendingSensorDelete.name}?` : ""}
        detail="This removes the sensor and its captured readings. The parent device is unaffected."
        confirmLabel="Delete sensor"
        cancelLabel="Cancel"
        tone="destructive"
        isLoading={isDeleting}
        onConfirm={confirmDeleteSensor}
        onCancel={() => setPendingSensorDelete(null)}
      />
    </motion.div>
  );
}

function EmptySidebar() {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 bg-surface-950 px-6 text-center">
      <Radio className="h-7 w-7 text-surface-600" />
      <p className="text-sm font-medium text-surface-300">Nothing selected</p>
      <p className="max-w-xs text-xs text-surface-500">
        Click a device to edit its details, or a sensor to view live readings, attached documents, 3D placements, and the full reading history.
      </p>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone = "default",
}: {
  label: string;
  value: number;
  icon: typeof Cpu;
  tone?: "default" | "ok";
}) {
  const chipClass =
    tone === "ok"
      ? "from-emerald-500/30 to-emerald-400/30"
      : "from-brand-500/30 to-purple-500/30";
  return (
    <div className="rounded-lg border border-surface-700/50 bg-surface-900/40 px-3 py-2">
      <div className="flex items-center gap-2.5">
        <div
          className={`flex h-8 w-8 items-center justify-center rounded-md bg-gradient-to-br ${chipClass} ring-1 ring-white/10`}
        >
          <Icon className="h-4 w-4 text-brand-200" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-surface-500">{label}</div>
          <div className="text-lg font-semibold text-surface-100">{value}</div>
        </div>
      </div>
    </div>
  );
}

function DeviceStatusBadge({ status }: { status: string }) {
  const tone =
    status === "active"
      ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30"
      : status === "faulty"
        ? "bg-red-500/15 text-red-300 ring-red-500/30"
        : status === "maintenance"
          ? "bg-amber-500/15 text-amber-300 ring-amber-500/30"
          : "bg-surface-800 text-surface-400 ring-surface-700";
  return (
    <span
      className={
        "inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 " +
        tone
      }
    >
      {status}
    </span>
  );
}

function ConnectionBadge({
  status,
  lastSeen,
}: {
  status: string | null;
  lastSeen: string | null;
}) {
  const tone =
    status === "connected"
      ? "bg-emerald-500/15 text-emerald-300"
      : status === "error"
        ? "bg-red-500/15 text-red-300"
        : "bg-surface-800 text-surface-400";
  return (
    <div
      className="hidden shrink-0 flex-col items-end gap-0.5 sm:flex"
      title={lastSeen ? `Last seen ${lastSeen}` : "No connection telemetry"}
    >
      <span
        className={
          "inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider " +
          tone
        }
      >
        <span
          className="h-1.5 w-1.5 rounded-full"
          style={{
            backgroundColor:
              status === "connected"
                ? "#34d399"
                : status === "error"
                  ? "#f87171"
                  : "#475569",
          }}
        />
        {status || "—"}
      </span>
      {lastSeen && (
        <span className="text-[9px] text-surface-500">{formatDate(lastSeen)}</span>
      )}
    </div>
  );
}

function SourcePill({ source }: { source: string }) {
  const tone =
    source === "simulated"
      ? "bg-amber-500/15 text-amber-300 ring-amber-500/30"
      : "bg-brand-500/15 text-brand-200 ring-brand-500/30";
  return (
    <span
      className={
        "inline-flex w-fit items-center gap-1 truncate rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ring-1 " +
        tone
      }
      title={`Source: ${source}`}
    >
      {source === "simulated" ? <Activity className="h-3 w-3" /> : <Box className="h-3 w-3" />}
      {source.replace(/_/g, " ")}
    </span>
  );
}

function AssetLinkPill({ label, assetId }: { label: string; assetId: string }) {
  return (
    <a
      href={`#/assets/${assetId}`}
      className="inline-flex max-w-[180px] items-center gap-1 truncate rounded-full bg-sky-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-sky-300 ring-1 ring-sky-500/30 hover:bg-sky-500/25"
      title={`Linked to asset ${label} — open in register`}
      onClick={(e) => e.stopPropagation()}
    >
      <LinkIcon className="h-3 w-3" />
      <span className="truncate">{label}</span>
    </a>
  );
}

function IconButton({
  onClick,
  title,
  children,
  tone = "neutral",
}: {
  onClick: () => void;
  title: string;
  children: React.ReactNode;
  tone?: "neutral" | "danger";
}) {
  const toneClass =
    tone === "danger"
      ? "text-red-400 hover:bg-red-500/15 hover:text-red-300"
      : "text-surface-400 hover:bg-white/5 hover:text-surface-100";
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={title}
      className={
        "flex h-7 w-7 items-center justify-center rounded-md transition-colors " + toneClass
      }
    >
      {children}
    </button>
  );
}

function TreeSensorRow({
  sensor,
  linkedAsset,
  linkedFloc,
  isSelected,
  onSelect,
  onDelete,
}: {
  sensor: Sensor;
  linkedAsset: AssetOption | null;
  linkedFloc: FlocOption | null;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
}) {
  const data = sensor.data || {};
  const value = (data as { value?: unknown }).value;
  const ts = (data as { ts?: string }).ts;
  const recent = sensor.data_history?.readings ?? [];

  return (
    <div
      className={
        "relative flex items-center gap-1.5 border-b border-surface-700/30 px-2 py-1.5 pl-10 transition-colors last:border-b-0 " +
        (isSelected ? "bg-brand-500/10" : "hover:bg-white/[0.03]")
      }
    >
      <span
        className={
          "absolute left-6 top-0 h-full w-px " +
          (isSelected ? "bg-brand-400/40" : "bg-surface-700/40")
        }
      />
      <span
        className={
          "absolute left-6 top-1/2 h-px w-3 " +
          (isSelected ? "bg-brand-400/40" : "bg-surface-700/40")
        }
      />
      <Radio
        className={
          "h-3 w-3 shrink-0 " + (isSelected ? "text-brand-300" : "text-surface-500")
        }
      />
      <button
        onClick={onSelect}
        className="min-w-0 flex-1 text-left"
        title="Open sensor in the sidebar"
      >
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={
              "truncate text-xs font-medium " +
              (isSelected ? "text-brand-100" : "text-surface-100")
            }
          >
            {sensor.name}
          </span>
          {sensor.sensor_type && (
            <span className="rounded bg-surface-800 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-surface-300">
              {sensor.sensor_type}
            </span>
          )}
          {sensor.source_channel && (
            <span className="rounded bg-cyan-500/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-cyan-200">
              {sensor.source_channel}
            </span>
          )}
          <DeviceStatusBadge status={sensor.status} />
          {/* FL chip then Asset chip — mirrors the device row. */}
          {linkedFloc && (
            <FlocPill
              label={`${linkedFloc.code} — ${linkedFloc.name}`}
              level={linkedFloc.level}
            />
          )}
          {linkedAsset && (
            <AssetLinkPill
              label={`${linkedAsset.asset_code} · ${linkedAsset.name}`}
              assetId={linkedAsset.id}
            />
          )}
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[10px] text-surface-400">
          <span className="font-mono text-brand-200">
            {typeof value === "number"
              ? value.toFixed(2)
              : value !== undefined
                ? String(value)
                : "—"}
            {sensor.unit && value !== undefined ? ` ${sensor.unit}` : ""}
          </span>
          {ts && <span className="text-surface-500">{formatDate(ts)}</span>}
          {recent.length > 0 && (
            <span className="text-surface-500">{recent.length} stored</span>
          )}
        </div>
      </button>
      <div className="flex shrink-0 items-center gap-0.5">
        <IconButton onClick={onSelect} title="Edit sensor in sidebar">
          <Pencil className="h-3.5 w-3.5" />
        </IconButton>
        <IconButton onClick={onDelete} title="Delete sensor" tone="danger">
          <Trash2 className="h-3.5 w-3.5" />
        </IconButton>
      </div>
    </div>
  );
}

/** Vertical label + control wrapper for one column of the filter bar.
 *  Width is parameterized so each column can size to its content kind. */
function FilterField({
  label,
  widthClass,
  children,
}: {
  label: string;
  widthClass: string;
  children: React.ReactNode;
}) {
  return (
    <div className={"flex flex-col gap-1 " + widthClass}>
      <label className="text-[9px] font-semibold uppercase tracking-wider text-surface-500">
        {label}
      </label>
      {children}
    </div>
  );
}

/** Plain `<select>` styled to match the filter bar. The empty option
 *  carries the placeholder text and represents "no filter". */
function FilterSelect({
  value,
  onChange,
  placeholder,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  options: { value: string; label: string }[];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={
        "w-full rounded-md border border-surface-700 bg-surface-900 px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 " +
        (value ? "text-surface-100" : "text-surface-500")
      }
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value} className="text-surface-100">
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** Functional-location chip rendered to the left of the asset chip on each
 *  row. Indigo to distinguish from the sky-blue asset chip; level prefix
 *  helps the user place the FL in the hierarchy at a glance. */
function FlocPill({ label, level }: { label: string; level: number }) {
  return (
    <span
      className="inline-flex max-w-[180px] items-center gap-1 truncate rounded-full bg-indigo-500/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-200 ring-1 ring-indigo-500/30"
      title={`Functional location · L${level} · ${label}`}
    >
      <MapPin className="h-3 w-3" />
      <span className="truncate">{label}</span>
    </span>
  );
}
